import React, {
  createContext,
  useContext,
  useCallback,
  useState,
  useEffect,
  useRef,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useReservationCountdown } from "../hooks/useReservationCountdown";
import {
  useCloneReservation,
  useExpireReservation,
  useGetReservation,
  useUpdateReservation,
} from "../api/api-hooks/reservation";
import { useBookingStore } from "../stores/bookingStore";
import { useConfiguration } from "./ConfigurationProvider";
import { ReservationWarningModal } from "../components/ReservationWarningModal";
import { ReservationTimeoutModal } from "../components/ReservationTimeoutModal";
import { useHeader } from "./HeaderContext";

interface ReservationSessionContextType {
  countdown: string | null;
  secondsRemaining: number;
  isExpired: boolean;
  isWarning: boolean;
}

const ReservationSessionContext = createContext<ReservationSessionContextType>({
  countdown: null,
  secondsRemaining: 0,
  isExpired: false,
  isWarning: false,
});

export const ReservationSessionProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { configuration, identifier } = useConfiguration();
  const {
    selectedStartDate,
    selectedEndDate,
    getGuestCount,
    getGuestsByType,
    selectedLanguage,
    wbeConfig,
    setSearchInitiated,
    setSelectedStartDate,
    setSelectedEndDate,
    replaceUnits,
  } = useHeader();
  const { resetReservation, reservationKey, setReservationKey } = useBookingStore();
  const { mutateAsync: updateReservation, isPending: isExtendingTime } =
    useUpdateReservation();
  const { mutateAsync: cloneReservation, isPending: isCloningReservation } =
    useCloneReservation();

  const [showWarning, setShowWarning] = useState(false);
  const [showTimeout, setShowTimeout] = useState(false);
  const [isRestoringSession, setIsRestoringSession] = useState(false);
  const [canSearchSameAgain, setCanSearchSameAgain] = useState(false);
  const hasShownWarning = useRef(false);
  const hasShownTimeout = useRef(false);
  const isOnConfirmationPage = location.pathname.includes("/confirmation");

  const { mutateAsync: expireReservation } = useExpireReservation();
  const { data: reservationData, refetch: refetchReservation } = useGetReservation({
    variables: {
      reservationKey: reservationKey || "",
      languageCode: "sv-SE",
      siteId: configuration?.siteId || "",
    },
    enabled: !!reservationKey && !!configuration?.siteId,
  });
  const isInBookingFlow =
    location.pathname.includes("/booking") || location.pathname.includes("/checkout");
  const hasActiveInitiatedReservation =
    reservationData?.status === "initiated" &&
    (reservationData?.rows?.length ?? 0) > 0;
  const shouldAllowRestore = isInBookingFlow && hasActiveInitiatedReservation;

  const formatLocalDate = useCallback(
    (date: Date) =>
      `${date.getFullYear()}-${(date.getMonth() + 1)
        .toString()
        .padStart(2, "0")}-${date.getDate().toString().padStart(2, "0")}`,
    [],
  );

  const parseYmdLocal = useCallback((raw: string): Date => {
    // Reservation dates come back as ISO datetimes ("2026-08-18T00:00:00Z"),
    // so grab just the YYYY-MM-DD prefix. Constructing via `new Date(iso)`
    // would shift the day in negative-offset zones.
    const [year, month, day] = raw.slice(0, 10).split("-").map(Number);
    return new Date(year, month - 1, day);
  }, []);

  const getRestartSearch = useCallback(() => {
    const existingParams = new URLSearchParams(location.search);
    if (existingParams.has("arrival") && existingParams.has("departure")) {
      return location.search;
    }

    // Fall back to the reservation's own dates/guests when HeaderContext is
    // empty (e.g. tab reloaded on landing without arrival/departure params).
    // Without this, "Sök samma igen" would navigate to a bare landing URL and
    // useSearch would have no dates to auto-trigger on.
    const firstRow = reservationData?.rows?.[0];
    const useReservationFallback = !selectedStartDate || !selectedEndDate;

    const arrivalDate = useReservationFallback
      ? firstRow?.arrivalDate?.slice(0, 10)
      : formatLocalDate(selectedStartDate!);
    const departureDate = useReservationFallback
      ? firstRow?.departureDate?.slice(0, 10)
      : formatLocalDate(selectedEndDate!);

    if (!arrivalDate || !departureDate) {
      return "";
    }

    const params = new URLSearchParams();
    params.set("arrival", arrivalDate);
    params.set("departure", departureDate);

    const adultCount = useReservationFallback
      ? firstRow?.numberOfGuests?.adults ?? 0
      : getGuestCount("adult");
    const teenagerCount = useReservationFallback
      ? firstRow?.numberOfGuests?.teenagers ?? 0
      : getGuestCount("teenager");
    // GET reservation currently drops bedCapacityType/age from guests
    // (backend/.../reservations/handler.ts:746), so child/infant guests can't
    // be faithfully reconstructed for a search — the search API requires
    // bedCapacityType. Fall back to omitting them; adults/teenagers still
    // narrow the search sensibly.
    const childGuests = useReservationFallback ? [] : getGuestsByType("child");
    const infantGuests = useReservationFallback ? [] : getGuestsByType("infant");

    if (adultCount > 0) {
      params.set("adults", adultCount.toString());
    }
    if (teenagerCount > 0) {
      params.set("teenagers", teenagerCount.toString());
    }
    // Skip guests whose age can't be resolved (config not loaded yet).
    // Better to omit than to write 0:bedtype that re-parses as an out-of-range
    // guest, which would re-trigger the validation error this PR fixes.
    const childMinAge = wbeConfig?.guestTypesConfig?.child?.minAge;
    const infantMinAge = wbeConfig?.guestTypesConfig?.infant?.minAge;
    const childrenParam = childGuests
      .map((g) => {
        const age = g.age ?? childMinAge;
        return age == null ? null : `${age}:${g.bedCapacityType}`;
      })
      .filter((s): s is string => s !== null)
      .join(",");
    if (childrenParam) {
      params.set("children", childrenParam);
    }
    const infantsParam = infantGuests
      .map((g) => {
        const age = g.age ?? infantMinAge;
        return age == null ? null : `${age}:${g.bedCapacityType}`;
      })
      .filter((s): s is string => s !== null)
      .join(",");
    if (infantsParam) {
      params.set("infants", infantsParam);
    }

    const existingLang = existingParams.get("lang");
    if (existingLang) {
      params.set("lang", existingLang);
    }

    return `?${params.toString()}`;
  }, [
    location.search,
    selectedStartDate,
    selectedEndDate,
    formatLocalDate,
    getGuestCount,
    getGuestsByType,
    wbeConfig?.guestTypesConfig,
    reservationData,
  ]);

  const emitTimeoutContactDraftSave = useCallback(() => {
    window.dispatchEvent(new Event("wbe-timeout-save-contact-draft"));
  }, []);

  const handleExpired = useCallback(() => {
    if (!isOnConfirmationPage) {
      setShowWarning(false);
      setShowTimeout(true);
      setCanSearchSameAgain(false);
      hasShownWarning.current = true;
      hasShownTimeout.current = true;
    }
  }, [isOnConfirmationPage]);

  const { countdown, isExpired, secondsRemaining, isWarning } =
    useReservationCountdown({
      updatedAt: reservationData?.updatedAt,
      status: reservationData?.status,
      onExpired: handleExpired,
    });

  useEffect(() => {
    if (
      isWarning &&
      !hasShownWarning.current &&
      !isExpired &&
      !hasShownTimeout.current &&
      !isRestoringSession &&
      !isOnConfirmationPage
    ) {
      setShowWarning(true);
      hasShownWarning.current = true;
    }
  }, [isWarning, isExpired, isOnConfirmationPage, isRestoringSession]);

  useEffect(() => {
    if (!isWarning && !hasShownTimeout.current) {
      hasShownWarning.current = false;
    }
  }, [isWarning]);

  useEffect(() => {
    setShowWarning(false);
    setShowTimeout(false);
    setCanSearchSameAgain(false);
    hasShownWarning.current = false;
    hasShownTimeout.current = false;
  }, [reservationKey]);

  const handleExtendTime = useCallback(async () => {
    if (!reservationKey || !configuration?.siteId) {
      return;
    }

    try {
      await updateReservation({
        reservationKey,
        data: {},
        siteId: configuration.siteId,
        languageCode: selectedLanguage,
      });

      setShowWarning(false);
    } catch (error) {
      console.error("Failed to extend reservation time:", error);
    }
  }, [reservationKey, configuration?.siteId, updateReservation, selectedLanguage]);

  const handleCancelBooking = useCallback(async () => {
    if (reservationKey && configuration?.siteId) {
      try {
        await expireReservation({
          reservationKey,
          siteId: configuration.siteId,
        });
      } catch (error) {
        console.error(
          "[handleCancelBooking] Failed to expire reservation",
          error,
        );
      }
    }

    resetReservation();
    setShowWarning(false);
    navigate(`/${identifier}`);
  }, [
    reservationKey,
    configuration?.siteId,
    expireReservation,
    resetReservation,
    navigate,
    identifier,
  ]);

  const handleRestoreBooking = useCallback(async () => {
    if (!reservationKey || !configuration?.siteId || isCloningReservation) {
      return;
    }

    setIsRestoringSession(true);
    try {
      const currentReservationKey = reservationKey;
      // Clone endpoint either touches a still-initiated reservation (same key)
      // or rebuilds an identical reservation from an aborted source (new key).
      const cloneResponse = await cloneReservation({
        reservationKey,
        siteId: configuration.siteId,
        languageCode: selectedLanguage,
      });

      if (cloneResponse.reservationKey !== currentReservationKey) {
        setReservationKey(cloneResponse.reservationKey);
      } else {
        // Same key path relies on backend "touch"; refetch before reenabling warnings.
        await refetchReservation();
      }

      setShowTimeout(false);
      setShowWarning(false);
      setCanSearchSameAgain(false);
      hasShownWarning.current = false;
      hasShownTimeout.current = false;
    } catch {
      // Clone failed (source key gone, capacity taken, etc.) — fall back to restart flow.
      setCanSearchSameAgain(true);
    } finally {
      setIsRestoringSession(false);
    }
  }, [
    reservationKey,
    configuration?.siteId,
    cloneReservation,
    setReservationKey,
    isCloningReservation,
    refetchReservation,
    selectedLanguage,
  ]);

  const handleSearchSameAgain = useCallback(async () => {
    emitTimeoutContactDraftSave();

    if (reservationKey && configuration?.siteId) {
      try {
        await expireReservation({
          reservationKey,
          siteId: configuration.siteId,
        });
      } catch (error) {
        console.error("[handleTimeout] Failed to expire reservation", error);
      }
    }

    resetReservation();
    setShowTimeout(false);
    setShowWarning(false);

    // If HeaderContext lost its dates/guests (e.g. tab was reloaded on
    // landing without any query params), rehydrate them from the reservation
    // so useSearch's auto-trigger has valid inputs to fire on. Without this
    // the search either silently no-ops (missing dates) or runs with default
    // guest counts instead of the ones in the URL (mismatched results).
    const firstRow = reservationData?.rows?.[0];
    if (!selectedStartDate && firstRow?.arrivalDate) {
      setSelectedStartDate(parseYmdLocal(firstRow.arrivalDate));
    }
    if (!selectedEndDate && firstRow?.departureDate) {
      setSelectedEndDate(parseYmdLocal(firstRow.departureDate));
    }
    // Rebuild units from the reservation counts (adults + teenagers only —
    // children/infants can't be safely reconstructed; see comment in
    // getRestartSearch). useSearch reads guest counts from HeaderContext.units,
    // not from the URL, so this must mirror the URL params we just wrote.
    if ((!selectedStartDate || !selectedEndDate) && firstRow?.numberOfGuests) {
      const rehydratedGuests = [
        ...Array.from({ length: firstRow.numberOfGuests.adults ?? 0 }, () => ({
          ageType: "adult" as const,
          bedCapacityType: "full" as const,
        })),
        ...Array.from({ length: firstRow.numberOfGuests.teenagers ?? 0 }, () => ({
          ageType: "teenager" as const,
          bedCapacityType: "full" as const,
        })),
      ];
      if (rehydratedGuests.length) {
        replaceUnits([{ id: crypto.randomUUID(), guests: rehydratedGuests }]);
      }
    }

    // HeaderContext's URL-parsing effect only runs on mount, so a same-app
    // navigation to landing won't re-trigger the search. Flip the flag
    // explicitly so useSearch's auto-trigger fires when landing renders.
    setSearchInitiated(true);
    navigate(`/${identifier}${getRestartSearch()}`);
  }, [
    reservationKey,
    configuration?.siteId,
    expireReservation,
    resetReservation,
    identifier,
    getRestartSearch,
    emitTimeoutContactDraftSave,
    navigate,
    setSearchInitiated,
    reservationData,
    selectedStartDate,
    selectedEndDate,
    setSelectedStartDate,
    setSelectedEndDate,
    parseYmdLocal,
    replaceUnits,
  ]);

  const handleGoToStart = useCallback(async () => {
    if (reservationKey && configuration?.siteId) {
      try {
        await expireReservation({
          reservationKey,
          siteId: configuration.siteId,
        });
      } catch (error) {
        console.error("[handleGoToStart] Failed to expire reservation", error);
      }
    }

    resetReservation();
    setShowTimeout(false);
    setShowWarning(false);
    navigate(`/${identifier}`);
  }, [
    reservationKey,
    configuration?.siteId,
    expireReservation,
    resetReservation,
    identifier,
    navigate,
  ]);

  return (
    <ReservationSessionContext.Provider
      value={{
        countdown,
        secondsRemaining,
        isExpired,
        isWarning,
      }}
    >
      {children}

      {!isOnConfirmationPage && reservationKey && (
        <>
          <ReservationWarningModal
            isOpen={showWarning}
            onExtend={handleExtendTime}
            onCancel={handleCancelBooking}
            timeRemaining={countdown || ""}
            isExtending={isExtendingTime}
          />

          <ReservationTimeoutModal
            isOpen={showTimeout}
            onRestoreBooking={handleRestoreBooking}
            onSearchSameAgain={handleSearchSameAgain}
            onGoToStart={handleGoToStart}
            showRestoreBooking={false}
            showSearchSameAgain={!shouldAllowRestore || canSearchSameAgain}
            isRestoring={isCloningReservation}
          />
        </>
      )}
    </ReservationSessionContext.Provider>
  );
};

// oxlint-disable-next-line react/only-export-components -- tests mock this module path for the hook
export const useReservationSession = () => {
  return useContext(ReservationSessionContext);
};
